import { useState, useEffect, ReactNode } from 'react';

type Column<T> = {
  key: keyof T | string;
  title: string;
  render?: (item: T, value: any) => ReactNode;
  searchable?: boolean;
};

type DataTableProps<T> = {
  data: T[];
  columns: Column<T>[];
  searchFields?: (keyof T)[];
  searchPlaceholder?: string;
  onRowClick?: (item: T) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  title?: string;
  emptyMessage?: string;
};

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  searchFields = [],
  searchPlaceholder = 'Поиск...',
  onRowClick,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [5, 10, 25, 50],
  title,
  emptyMessage = 'Нет данных'
}: DataTableProps<T>) {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filtered, setFiltered] = useState<T[]>(data);

  // Filter data based on search
  useEffect(() => {
    if (!search.trim()) {
      setFiltered(data);
    } else {
      const query = search.toLowerCase();
      setFiltered(data.filter(item => {
        if (searchFields.length > 0) {
          return searchFields.some(field => {
            const value = item[field];
            return value && String(value).toLowerCase().includes(query);
          });
        }
        // Fallback to all string fields
        return Object.values(item).some(value => 
          value && String(value).toLowerCase().includes(query)
        );
      }));
    }
    setCurrentPage(1);
  }, [search, data, searchFields]);

  // Update filtered data when data changes
  useEffect(() => {
    setFiltered(data);
  }, [data]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filtered.length);
  const currentData = filtered.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <div className="card">
      {(title || search || pageSizeOptions.length > 1) && (
        <div className="toolbar">
          {title && <h3 style={{margin:0}}>{title}</h3>}
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            {searchFields.length > 0 && (
              <input 
                type="text" 
                placeholder={searchPlaceholder}
                value={search} 
                onChange={e => setSearch(e.target.value)}
                style={{width:300}}
              />
            )}
            {pageSizeOptions.length > 1 && (
              <select value={pageSize} onChange={e => handlePageSizeChange(Number(e.target.value))}>
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
      
      <table className="table">
        <thead>
          <tr>
            {columns.map((col, index) => (
              <th key={index}>{col.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {currentData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{textAlign:'center', color:'var(--muted)', padding:'20px'}}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            currentData.map((item, index) => (
              <tr 
                key={index} 
                style={{cursor: onRowClick ? 'pointer' : 'default'}} 
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((col, colIndex) => {
                  const value = typeof col.key === 'string' ? item[col.key] : item[col.key as keyof T];
                  return (
                    <td key={colIndex}>
                      {col.render ? col.render(item, value) : String(value ?? '')}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {filtered.length > pageSize && (
        <div style={{marginTop:16, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <span style={{color:'var(--muted)'}}>
            Показано {startIndex + 1}-{endIndex} из {filtered.length}
          </span>
          <div style={{display:'flex', gap:8}}>
            <button 
              className="secondary" 
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ←
            </button>
            <span style={{padding:'8px 12px'}}>{currentPage}</span>
            <button 
              className="secondary" 
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
